import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend
} from 'recharts';

/**
 * BACKEND INTEGRATION NOTES FOR CURSOR:
 * 
 * This component expects data in the following format:
 * 
 * @param {Array} data - Array of category objects with name and percentage value
 * @example
 * const data = [
 *   { 
 *     name: "Generic Product Catalog Inquiry",  // Category name
 *     value: 28.5,                             // Percentage value
 *     count: 342                               // Optional: actual message count
 *   },
 *   { name: "Competitor/Replacement Parts", value: 18.3, count: 219 },
 *   { name: "Product Details or Manual", value: 14.7, count: 176 },
 *   // ... more categories
 * ]
 * 
 * API ENDPOINT SUGGESTION:
 * GET /api/messages/categories?startDate={startDate}&endDate={endDate}&client={clientKey}&userType=external
 * 
 * RESPONSE FORMAT:
 * {
 *   "success": true,
 *   "data": [
 *     { "name": "Generic Product Catalog Inquiry", "value": 28.5, "count": 342 },
 *     { "name": "Competitor/Replacement Parts", "value": 18.3, "count": 219 }
 *   ],
 *   "total": 1200  // Total messages analyzed
 * }
 * 
 * INTEGRATION POINT: monitor-frontend/src/pages/monitoring/Index.js
 * Look for: <PieChartPlaceholder data={[]} />
 * Replace with: <PieChartPlaceholder data={messageCategories} />
 * 
 * CATEGORY MAPPING (for reference):
 * - Generic Product Catalog Inquiry
 * - Competitor/Replacement Parts
 * - Product Details or Manual
 * - Order Management
 * - Current Branch Availability/Pricing
 * - Product Information
 * - General Conversation
 * - Out of Scope
 */

const PieChartPlaceholder = ({ 
  title = "External User Query Categories",
  data = [],
  showLegend = true,
  colors = [
    "#3B82F6", // Blue
    "#F59E0B", // Yellow
    "#10B981", // Green
    "#EF4444", // Red
    "#8B5CF6", // Purple
    "#EC4899", // Pink
    "#14B8A6", // Teal
    "#F97316", // Orange
  ]
}) => {
  // Process data for the chart
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // Handle the new data structure from useMonitoringData
    return data.map((item, index) => ({
      name: item.name,
      value: item.percentage || item.value || 0, // Support both new and old formats
      count: item.count || 0,
      color: colors[index % colors.length]
    }));
  }, [data, colors]);

  // Custom tooltip to show percentage and count
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-2 border border-gray-200 shadow-sm rounded-md">
          <p className="text-sm font-semibold">{data.name}</p>
          <p className="text-sm" style={{ color: data.color }}>
            {data.value}% ({data.count} messages)
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="p-6 flex-1">
      <CardHeader className="p-0 mb-4">
        <CardTitle className="text-lg font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-0 flex-1 flex flex-col">
        {/* Pie Chart */}
        {data && data.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  outerRadius={77}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ value }) => `${value}%`}
                  labelLine={false}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>

            {/* Legend */}
            {showLegend && (
              <div className="space-y-2 max-h-40 overflow-y-auto mt-4 flex-1">
                {chartData.slice(0, 8).map((category, index) => (
                  <div key={index} className="flex items-center justify-between text-xs">
                    <div className="flex items-center space-x-2 flex-1">
                      <div 
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: category.color }}
                      ></div>
                      <span className="text-gray-600 truncate">{category.name}</span>
                    </div>
                    <span className="text-gray-800 font-medium ml-2">
                      {category.value}% ({category.count})
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="h-48 bg-gray-50 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-200 mb-4">
            <div className="text-center">
              <div className="text-gray-400 mb-2">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                </svg>
              </div>
              <p className="text-gray-500 text-sm">No external user query data available for the selected date range</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PieChartPlaceholder; 